import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import FormattedDateInput from '../../components/ui/FormattedDateInput';
import FormattedDateRangeInput from '../../components/ui/FormattedDateRangeInput';
import WizardSelect from '../../components/ui/WizardSelect';
import { ReviewUploadedFilesTable } from '../../components/wizard/ReviewUploadedFilesTable';
import { WizardTempFileUploadList } from '../../components/wizard/WizardTempFileUploadList';
import {
  ReviewChips,
  ReviewField,
  ReviewGrid,
  ReviewSection,
  ReviewSubBlock,
} from '../../components/criminal/CriminalReviewUi';
import * as attorneyGeneralService from '../../services/attorneyGeneralCaseRequests';
import {
  getAttorneyGeneralRequestViewData,
  saveAttorneyGeneralRequest,
  type AttorneyGeneralRequestViewData,
} from '../../services/attorneyGeneralCaseRequests';
import type { AttorneyGeneralSaveStep11 } from '../../types/attorneyGeneralApi';
import {
  newAttorneyGeneralAccused,
  type AttorneyGeneralInlineUpload,
  type AttorneyGeneralWizardDraft,
} from '../../types/attorneyGeneralCaseRequest';
import type { AccusedRecord } from '../../types/criminalCaseRequest';
import {
  calcAgeFromDob,
  contactNumberError,
  sliceMax,
  todayIsoDateLocal,
} from '../../utils/caseFieldValidation';
import { formatDisplayDateTime } from '../../utils/dateFormat';
import { setFlashToast } from '../../utils/flashToast';
import { focusFirstInvalid } from '../../utils/requiredValidation';
import {
  isBriberySubCategory,
  isOtherCourtType,
  type AttorneyGeneralWizardStep,
} from '../wizard/attorneyGeneral/attorneyGeneralConstants';
import {
  isHighUrgencyLevel,
  isOtherNextStep,
  isStatusOther,
  MAX_NUMBER_OF_RESPONDENTS_DIGITS,
  onlyDigitsMax,
  preferredAccessLabels,
  requiresCourtType,
  requiresCustodialAuthority,
} from '../wizard/attorneyGeneral/attorneyGeneralHelpers';
import {
  minDeadlineDateForAttorneyGeneral,
  validateAttorneyGeneralStep,
} from '../wizard/attorneyGeneral/attorneyGeneralStepValidation';
import { useAttorneyGeneralLookups } from '../wizard/attorneyGeneral/useAttorneyGeneralLookups';
import { translatePreferredAccessName } from '../wizard/rightToInformation/rightToInformationHelpers';
import { submitterFromApiStep } from '../view-request/viewRequestHelpers';
import { useWizardFieldLabels } from '../wizard/useWizardFieldLabels';
import { FieldError, inpClass } from './criminalFieldUi';
import {
  confirmCaseUpdateCancel,
  formatSubmittedAtLabel,
  isCaseRequestUuid,
  mergeSavedStepIntoInitialBaseline,
  toSelectOpts,
} from './taskCaseUpdateHelpers';
import TaskCaseUpdateSectionActions from './TaskCaseUpdateSectionActions';

type Props = {
  caseRequestId: string;
  recentSubmittedAt: string;
  readOnly?: boolean;
  viewData?: AttorneyGeneralRequestViewData | null;
  viewDataLoading?: boolean;
  onCancelConfirmed?: () => void;
};

const MAX_DESC = 2000;
const MAX_REF = 255;
const MAX_CONTACT = 10;
type EditableStep = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;

function TrashIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
  );
}

function cloneDraft(d: AttorneyGeneralWizardDraft): AttorneyGeneralWizardDraft {
  return {
    ...d,
    applicableAreas: [...d.applicableAreas],
    applicableAreaIds: [...d.applicableAreaIds],
    previousAdviceUploads: d.previousAdviceUploads.map((f) => ({ ...f })),
    judgementOrderUploads: d.judgementOrderUploads.map((f) => ({ ...f })),
    accused: d.accused.map((a) => ({ ...a })),
    rtiPreferredMethodOfAccessIds: [...d.rtiPreferredMethodOfAccessIds],
    rtiPreferredMethodOfAccessNames: [...d.rtiPreferredMethodOfAccessNames],
    deliveryMethodIds: [...d.deliveryMethodIds],
    deliveryMethods: [...d.deliveryMethods],
    documents: d.documents.map((doc) => ({ ...doc })),
  };
}

function draftHasUnsavedChanges(
  current: AttorneyGeneralWizardDraft,
  saved: AttorneyGeneralWizardDraft,
): boolean {
  return JSON.stringify(current) !== JSON.stringify(saved);
}

function lookupName(items: { id: string; name: string }[], id: string): string {
  return items.find((x) => x.id === id)?.name ?? '';
}

function mergeLookupItems(
  items: { id: string; name: string; code?: string }[],
  selectedId: string,
  selectedName: string,
): { id: string; name: string; code?: string }[] {
  if (!selectedId || items.some((x) => x.id === selectedId)) return items;
  return [...items, { id: selectedId, name: selectedName || selectedId }];
}

function pickStep1Snapshot(d: AttorneyGeneralWizardDraft) {
  return {
    description: d.description,
    provinceId: d.provinceId,
    districtId: d.districtId,
    caseSource: d.caseSource,
    highCourtId: d.highCourtId,
    magistrateCourtId: d.magistrateCourtId,
    policeStationId: d.policeStationId,
    countryInvolvedId: d.countryInvolvedId,
    directionOfRequest: d.directionOfRequest,
    yourReferenceNumber: d.yourReferenceNumber,
    otherExternalReferences: d.otherExternalReferences,
    hasPreviousAdvice: d.hasPreviousAdvice,
    previousAdviceUploads: d.previousAdviceUploads.map((f) => ({
      id: f.id,
      fileName: f.fileName,
      originalFileName: f.originalFileName,
    })),
    hasConnectedAdviceCase: d.hasConnectedAdviceCase,
    connectedAdviceDetails: d.connectedAdviceDetails,
    hasActionInstituted: d.hasActionInstituted,
    courtTypeId: d.courtTypeId,
    courtTypeOther: d.courtTypeOther,
    courtId: d.courtId,
    isAppealMatter: d.isAppealMatter,
    originalCourt: d.originalCourt,
    judgementOrderUploads: d.judgementOrderUploads.map((f) => ({
      id: f.id,
      fileName: f.fileName,
      originalFileName: f.originalFileName,
    })),
    caseNumber: d.caseNumber,
    hasConnectedMatters: d.hasConnectedMatters,
    connectedCaseNumber: d.connectedCaseNumber,
    connectedAgdFileRef: d.connectedAgdFileRef,
  };
}

function pickStep2Snapshot(d: AttorneyGeneralWizardDraft) {
  return {
    partyBodyType: d.partyBodyType,
    plaintiffName: d.plaintiffName,
    defendantName: d.defendantName,
    otherInvolvedInstitutions: d.otherInvolvedInstitutions,
    legalOfficerName: d.legalOfficerName,
    legalOfficerContact: d.legalOfficerContact,
  };
}

function pickStep3Snapshot(d: AttorneyGeneralWizardDraft) {
  return {
    subjectOfDispute: d.subjectOfDispute,
    incidentDateFrom: d.incidentDateFrom,
    incidentDateTo: d.incidentDateTo,
    nextStepOfCaseId: d.nextStepOfCaseId,
    nextStepOfCaseOther: d.nextStepOfCaseOther,
    nextCaseDate: d.nextCaseDate,
  };
}

function pickStep4Snapshot(d: AttorneyGeneralWizardDraft) {
  return {
    victimName: d.victimName,
    victimDob: d.victimDob,
    victimGenderId: d.victimGenderId,
  };
}

function pickStep5Snapshot(d: AttorneyGeneralWizardDraft) {
  return {
    offenceDateFrom: d.offenceDateFrom,
    offenceDateTo: d.offenceDateTo,
    placeOfOffence: d.placeOfOffence,
    accused: d.accused.map((a) => ({
      name: a.name,
      status: a.status,
      statusOther: a.statusOther,
    })),
  };
}

function pickStep6Snapshot(d: AttorneyGeneralWizardDraft) {
  return {
    accusedName: d.accusedName,
    nationalityId: d.nationalityId,
    countryOfOriginId: d.countryOfOriginId,
    accusedDob: d.accusedDob,
    accusedGenderId: d.accusedGenderId,
    passportOrIdNumber: d.passportOrIdNumber,
    custodyStatusId: d.custodyStatusId,
    custodialAuthority: d.custodialAuthority,
    lastKnownLocation: d.lastKnownLocation,
  };
}

function pickStep7Snapshot(d: AttorneyGeneralWizardDraft) {
  return {
    arrestWarrantAvailable: d.arrestWarrantAvailable,
    warrantReference: d.warrantReference,
    applicableTreatyArrangement: d.applicableTreatyArrangement,
  };
}

function pickStep8Snapshot(d: AttorneyGeneralWizardDraft) {
  return {
    petitionerName: d.petitionerName,
    petitionerNic: d.petitionerNic,
    petitionerAddress: d.petitionerAddress,
    petitionerContact: d.petitionerContact,
    petitionerEmail: d.petitionerEmail,
    representedByAttorney: d.representedByAttorney,
    attorneyName: d.attorneyName,
    attorneyContact: d.attorneyContact,
    respondentNames: d.respondentNames,
    respondentType: d.respondentType,
    respondentAddress: d.respondentAddress,
    numberOfRespondents: d.numberOfRespondents,
  };
}

function pickStep9Snapshot(d: AttorneyGeneralWizardDraft) {
  return {
    rtiRequestorName: d.rtiRequestorName,
    rtiNicOrPassport: d.rtiNicOrPassport,
    rtiAddress: d.rtiAddress,
    rtiContactNumber: d.rtiContactNumber,
    rtiEmailAddress: d.rtiEmailAddress,
    rtiIsSriLankanCitizen: d.rtiIsSriLankanCitizen,
    rtiSpecificPublicAuthorityName: d.rtiSpecificPublicAuthorityName,
    rtiRequestedInformationDetails: d.rtiRequestedInformationDetails,
    rtiInformationRequestedTimePeriod: d.rtiInformationRequestedTimePeriod,
    rtiPreferredMethodOfAccessIds: [...d.rtiPreferredMethodOfAccessIds],
    rtiPreferredLanguageId: d.rtiPreferredLanguageId,
    rtiInvolvesLifeOrPersonalLiberty: d.rtiInvolvesLifeOrPersonalLiberty,
    rtiJustification: d.rtiJustification,
    rtiAdditionalNotes: d.rtiAdditionalNotes,
  };
}

function pickStep10Snapshot(d: AttorneyGeneralWizardDraft) {
  return {
    urgencyLevelId: d.urgencyLevelId,
    deadline: d.deadline,
    urgencyJustification: d.urgencyJustification,
  };
}

const STEP_PICKERS = {
  1: pickStep1Snapshot,
  2: pickStep2Snapshot,
  3: pickStep3Snapshot,
  4: pickStep4Snapshot,
  5: pickStep5Snapshot,
  6: pickStep6Snapshot,
  7: pickStep7Snapshot,
  8: pickStep8Snapshot,
  9: pickStep9Snapshot,
  10: pickStep10Snapshot,
} as const;

function hasNonEmptyValue(value: unknown): boolean {
  if (value == null) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return true;
  if (Array.isArray(value)) return value.some((item) => hasNonEmptyValue(item));
  if (typeof value === 'object') return Object.values(value).some((item) => hasNonEmptyValue(item));
  return false;
}

function stepHasData(step: EditableStep, d: AttorneyGeneralWizardDraft): boolean {
  return hasNonEmptyValue(STEP_PICKERS[step](d));
}

function canSaveStep(
  step: EditableStep,
  draft: AttorneyGeneralWizardDraft | null,
  initial: AttorneyGeneralWizardDraft | null,
): boolean {
  if (!draft || !initial) return false;
  const pick = STEP_PICKERS[step];
  const changed = JSON.stringify(pick(draft)) !== JSON.stringify(pick(initial));
  return (stepHasData(step, draft) || stepHasData(step, initial)) && changed;
}

function YesNoRadios({
  name,
  value,
  disabled,
  onChange,
}: {
  name: string;
  value: '' | 'yes' | 'no';
  disabled?: boolean;
  onChange: (v: 'yes' | 'no') => void;
}) {
  return (
    <div className="ccr-radio-row">
      {(['yes', 'no'] as const).map((v) => (
        <label key={v} className="ccr-radio">
          <input
            type="radio"
            name={name}
            checked={value === v}
            disabled={disabled}
            onChange={() => onChange(v)}
          />
          {v === 'yes' ? 'Yes' : 'No'}
        </label>
      ))}
    </div>
  );
}

export default function TaskCaseAttorneyGeneralUpdateTab({
  caseRequestId,
  recentSubmittedAt,
  readOnly = false,
  viewData,
  viewDataLoading = false,
  onCancelConfirmed,
}: Props) {
  const { t } = useTranslation();
  const f = useWizardFieldLabels();
  const [draft, setDraft] = useState<AttorneyGeneralWizardDraft | null>(null);
  const [initial, setInitial] = useState<AttorneyGeneralWizardDraft | null>(null);
  const [step11, setStep11] = useState<AttorneyGeneralSaveStep11 | undefined>();
  const [submittedLabel, setSubmittedLabel] = useState(recentSubmittedAt);
  const [loadedForKey, setLoadedForKey] = useState<string | null>(null);
  const [loadError, setLoadError] = useState('');
  const [savingStep, setSavingStep] = useState<AttorneyGeneralWizardStep | null>(null);
  const saveInFlightRef = useRef(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const isSaving = savingStep !== null;

  const lookups = useAttorneyGeneralLookups(
    'review',
    draft?.provinceId ?? '',
    draft?.districtId ?? '',
    draft?.courtTypeId ?? '',
  );
  const minDeadlineDate = useMemo(() => minDeadlineDateForAttorneyGeneral(), []);
  const maxDob = useMemo(() => todayIsoDateLocal(), []);
  const maxOffenceDate = useMemo(() => todayIsoDateLocal(), []);

  const useSharedViewData = viewData !== undefined || viewDataLoading;
  const loadKey = `${caseRequestId}|${recentSubmittedAt}|${viewData?.submittedAt ?? ''}|${viewData?.draft.draftRequestId ?? ''}`;
  const loading = useSharedViewData
    ? viewDataLoading || loadedForKey !== loadKey
    : loadedForKey !== loadKey;

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!isCaseRequestUuid(caseRequestId)) {
        if (cancelled) return;
        setDraft(null);
        setInitial(null);
        setLoadError('Case request id is missing or invalid.');
        setLoadedForKey(loadKey);
        return;
      }

      if (useSharedViewData) {
        if (viewDataLoading) return;
        if (!viewData?.draft) {
          if (cancelled) return;
          setDraft(null);
          setInitial(null);
          setStep11(undefined);
          setLoadError('Could not load case update data.');
          setLoadedForKey(loadKey);
          return;
        }
        if (cancelled) return;
        const loaded = cloneDraft(viewData.draft);
        setDraft(loaded);
        setInitial(cloneDraft(loaded));
        setStep11(viewData.step11);
        setSubmittedLabel(formatSubmittedAtLabel(viewData.submittedAt, recentSubmittedAt));
        setLoadError('');
        setLoadedForKey(loadKey);
        return;
      }

      const res = await getAttorneyGeneralRequestViewData(caseRequestId);
      if (cancelled) return;
      if (!res.success || !res.data?.draft) {
        setDraft(null);
        setInitial(null);
        setStep11(undefined);
        setLoadError((!res.success ? res.message : undefined) ?? 'Could not load case update data.');
        setLoadedForKey(loadKey);
        return;
      }
      const loaded = cloneDraft(res.data.draft);
      setDraft(loaded);
      setInitial(cloneDraft(loaded));
      setStep11(res.data.step11);
      setSubmittedLabel(formatSubmittedAtLabel(res.data.submittedAt, recentSubmittedAt));
      setLoadError('');
      setLoadedForKey(loadKey);
    })();
    return () => {
      cancelled = true;
    };
  }, [caseRequestId, loadKey, recentSubmittedAt, useSharedViewData, viewData, viewDataLoading]);

  const canSave1 = useMemo(() => canSaveStep(1, draft, initial), [draft, initial]);
  const canSave2 = useMemo(() => canSaveStep(2, draft, initial), [draft, initial]);
  const canSave3 = useMemo(() => canSaveStep(3, draft, initial), [draft, initial]);
  const canSave4 = useMemo(() => canSaveStep(4, draft, initial), [draft, initial]);
  const canSave5 = useMemo(() => canSaveStep(5, draft, initial), [draft, initial]);
  const canSave6 = useMemo(() => canSaveStep(6, draft, initial), [draft, initial]);
  const canSave7 = useMemo(() => canSaveStep(7, draft, initial), [draft, initial]);
  const canSave8 = useMemo(() => canSaveStep(8, draft, initial), [draft, initial]);
  const canSave9 = useMemo(() => canSaveStep(9, draft, initial), [draft, initial]);
  const canSave10 = useMemo(() => canSaveStep(10, draft, initial), [draft, initial]);
  const canSave: Record<EditableStep, boolean> = {
    1: canSave1,
    2: canSave2,
    3: canSave3,
    4: canSave4,
    5: canSave5,
    6: canSave6,
    7: canSave7,
    8: canSave8,
    9: canSave9,
    10: canSave10,
  };

  const clearErr = (key: string) => setErrors((e) => ({ ...e, [key]: '' }));

  const saveSection = async (step: EditableStep) => {
    if (!draft || readOnly || saveInFlightRef.current) return;
    const result = validateAttorneyGeneralStep(step, draft, {
      suspectStatuses: lookups.suspectStatuses,
    });
    const errors = result.errors;
    const specs = result.specs;
    setErrors(errors);
    if (Object.keys(errors).length) {
      focusFirstInvalid(specs, errors);
      return;
    }

    saveInFlightRef.current = true;
    setSavingStep(step);
    try {
      const r = await saveAttorneyGeneralRequest(draft, {
        isDraft: false,
        confirmedCorrect: true,
        currentStep: step,
        isUpdateAlreadySubmitted: true,
      });
      if (!r.success) {
        setFlashToast({ message: r.message ?? 'Failed to save case update.', variant: 'error' });
        return;
      }
      const next = {
        ...draft,
        draftRequestId: r.data.requestId || draft.draftRequestId,
        referenceNo: r.data.fileReferenceNumber ?? draft.referenceNo,
      };
      setDraft(next);
      setInitial((prev) => {
        if (!prev) return cloneDraft(next);
        return mergeSavedStepIntoInitialBaseline(prev, next, STEP_PICKERS[step], cloneDraft);
      });
      setErrors({});
      setFlashToast({ message: 'Case update saved successfully.', variant: 'success' });
    } finally {
      saveInFlightRef.current = false;
      setSavingStep(null);
    }
  };

  const resetSection = () => {
    if (initial) {
      setDraft(cloneDraft(initial));
      setErrors({});
    }
  };

  const beginCancel = () => {
    if (isSaving || !draft || !initial) return;
    if (draftHasUnsavedChanges(draft, initial)) {
      setCancelConfirmOpen(true);
      return;
    }
    resetSection();
  };

  const updateAccused = (id: string, patch: Partial<AccusedRecord>) => {
    setDraft((d) =>
      d ? { ...d, accused: d.accused.map((a) => (a.id === id ? { ...a, ...patch } : a)) } : d,
    );
  };

  const togglePreferredAccess = (id: string) => {
    setDraft((d) => {
      if (!d) return d;
      const has = d.rtiPreferredMethodOfAccessIds.includes(id);
      const ids = has
        ? d.rtiPreferredMethodOfAccessIds.filter((x) => x !== id)
        : [...d.rtiPreferredMethodOfAccessIds, id];
      const names = preferredAccessLabels(ids, lookups.preferredMethodOfAccess);
      return { ...d, rtiPreferredMethodOfAccessIds: ids, rtiPreferredMethodOfAccessNames: names };
    });
    clearErr('rtiPreferredMethodOfAccessIds');
  };

  if (loading) return <p className="tcd-update-ts">Loading case update…</p>;
  if (!draft) {
    return (
      <div className="tcd-case-update-error" role="alert">
        <p className="tcd-update-ts" style={{ color: 'var(--red)', margin: 0 }}>
          {loadError || 'Could not load case update data.'}
        </p>
      </div>
    );
  }

  const showHighCourt = isBriberySubCategory(draft.subFileCategoryName);
  const showCourtType = requiresCourtType(draft.hasActionInstituted);
  const showCourtName = draft.hasActionInstituted === 'yes';
  const showCourtOther = showCourtType && isOtherCourtType(draft.courtTypeId, draft.courtTypeName);
  const showNextStepOther = isOtherNextStep(draft.nextStepOfCaseName, draft.nextStepOfCaseId);
  const urgencyJustificationRequired = isHighUrgencyLevel(draft.urgencyLevelName);
  const accusedAge = draft.accusedDob ? calcAgeFromDob(draft.accusedDob) : null;
  const custodyOptions = mergeLookupItems(
    lookups.custodyStatuses,
    draft.custodyStatusId,
    draft.custodyStatusName,
  );
  const selectedCustody = custodyOptions.find((s) => s.id === draft.custodyStatusId);
  const showCustodialAuthority = requiresCustodialAuthority(
    draft.custodyStatusName,
    selectedCustody?.code,
  );
  const submitter = submitterFromApiStep(step11);
  const sectionActions = (step: EditableStep) => (
    <TaskCaseUpdateSectionActions
      canSave={canSave[step]}
      saving={savingStep === step}
      readOnly={readOnly}
      onSave={() => void saveSection(step)}
      onCancel={beginCancel}
    />
  );

  return (
    <>
      <ConfirmDialog
        isOpen={cancelConfirmOpen}
        title="Cancel Changes"
        message="Are you sure you want to cancel? Any unsaved changes will be lost."
        onClose={() => setCancelConfirmOpen(false)}
        onConfirm={() => {
          confirmCaseUpdateCancel(resetSection, () => setCancelConfirmOpen(false), onCancelConfirmed);
        }}
      />

      <div className="tcd-case-update">
        <p className="tcd-update-ts">
          Request Submitted at:{' '}
          <strong>{submittedLabel || formatDisplayDateTime(recentSubmittedAt) || '—'}</strong>
        </p>

        {/* Step 01 — Case Overview (editable; categorization read-only) */}
        <ReviewSection step="Step 01" title="Case Overview">
          <ReviewSubBlock title="Case Categorization">
            <ReviewGrid cols={4}>
              <ReviewField label="File Category" value={draft.fileCategory} />
              <ReviewField label="Sub File Category" value={draft.subFileCategoryName} />
            </ReviewGrid>
            <ReviewChips items={draft.applicableAreas} />
          </ReviewSubBlock>

          <ReviewSubBlock title="Case Details">
            <div className="ccr-fg" style={{ marginBottom: 14 }}>
              <label>Description</label>
              <textarea
                id="ag-description"
                className={inpClass(Boolean(errors.description))}
                rows={4}
                maxLength={MAX_DESC}
                value={draft.description}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => {
                  setDraft((d) => (d ? { ...d, description: sliceMax(e.target.value, MAX_DESC) } : d));
                  clearErr('description');
                }}
              />
              <FieldError message={errors.description} />
            </div>

            <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
              <div className="ccr-fg">
                <label>Province</label>
                <WizardSelect
                  id="ag-province"
                  value={draft.provinceId}
                  disabled={readOnly}
                  placeholder="Select Province"
                  options={toSelectOpts(lookups.provinces)}
                  onChange={(id) => {
                    setDraft((d) =>
                      d
                        ? {
                            ...d,
                            provinceId: id,
                            provinceName: lookupName(lookups.provinces, id),
                            districtId: '',
                            districtName: '',
                            magistrateCourtId: '',
                            magistrateCourtName: '',
                            highCourtId: '',
                            highCourtName: '',
                            policeStationId: '',
                            policeStationName: '',
                            courtId: '',
                            courtName: '',
                          }
                        : d,
                    );
                  }}
                />
              </div>
              <div className="ccr-fg">
                <label>District</label>
                <WizardSelect
                  id="ag-district"
                  value={draft.districtId}
                  disabled={readOnly || !draft.provinceId}
                  placeholder={draft.provinceId ? 'Select District' : 'Select province first'}
                  options={toSelectOpts(lookups.districts)}
                  onChange={(id) => {
                    setDraft((d) =>
                      d
                        ? {
                            ...d,
                            districtId: id,
                            districtName: lookupName(lookups.districts, id),
                            magistrateCourtId: '',
                            magistrateCourtName: '',
                            highCourtId: '',
                            highCourtName: '',
                            policeStationId: '',
                            policeStationName: '',
                            courtId: '',
                            courtName: '',
                          }
                        : d,
                    );
                  }}
                />
              </div>
            </div>

            <div className="ccr-fg" style={{ marginBottom: 14 }}>
              <label>Case Source</label>
              <textarea
                id="ag-caseSource"
                className={inpClass(Boolean(errors.caseSource))}
                rows={3}
                maxLength={MAX_DESC}
                value={draft.caseSource}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => {
                  setDraft((d) => (d ? { ...d, caseSource: sliceMax(e.target.value, MAX_DESC) } : d));
                  clearErr('caseSource');
                }}
              />
              <FieldError message={errors.caseSource} />
            </div>

            <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
              <div className="ccr-fg">
                <label>Magistrate Court Name</label>
                <WizardSelect
                  id="ag-magistrateCourt"
                  value={draft.magistrateCourtId}
                  disabled={readOnly || !draft.districtId}
                  placeholder="Select Magistrate Court"
                  options={toSelectOpts(lookups.magistrateCourts)}
                  onChange={(id) =>
                    setDraft((d) =>
                      d
                        ? {
                            ...d,
                            magistrateCourtId: id,
                            magistrateCourtName: lookupName(lookups.magistrateCourts, id),
                          }
                        : d,
                    )
                  }
                />
              </div>
              {showHighCourt ? (
                <div className="ccr-fg">
                  <label>High Court Name</label>
                  <WizardSelect
                    id="ag-highCourt"
                    value={draft.highCourtId}
                    disabled={readOnly || !draft.districtId}
                    placeholder="Select High Court"
                    options={toSelectOpts(lookups.highCourts)}
                    onChange={(id) =>
                      setDraft((d) =>
                        d
                          ? {
                              ...d,
                              highCourtId: id,
                              highCourtName: lookupName(lookups.highCourts, id),
                            }
                          : d,
                      )
                    }
                  />
                </div>
              ) : null}
            </div>

            <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
              <div className="ccr-fg">
                <label>Police Station</label>
                <WizardSelect
                  id="ag-policeStation"
                  value={draft.policeStationId}
                  disabled={readOnly || !draft.districtId}
                  placeholder="Select Police Station"
                  options={toSelectOpts(lookups.policeStations)}
                  onChange={(id) =>
                    setDraft((d) =>
                      d
                        ? {
                            ...d,
                            policeStationId: id,
                            policeStationName: lookupName(lookups.policeStations, id),
                          }
                        : d,
                    )
                  }
                />
              </div>
              <div className="ccr-fg">
                <label>Country Involved</label>
                <WizardSelect
                  id="ag-countryInvolved"
                  value={draft.countryInvolvedId}
                  disabled={readOnly}
                  placeholder="Select Country"
                  options={toSelectOpts(lookups.countries)}
                  onChange={(id) =>
                    setDraft((d) =>
                      d
                        ? {
                            ...d,
                            countryInvolvedId: id,
                            countryInvolvedName: lookupName(lookups.countries, id),
                          }
                        : d,
                    )
                  }
                />
              </div>
            </div>

            <div className="ccr-fg" style={{ marginBottom: 14 }}>
              <label>Direction of Request</label>
              <textarea
                id="ag-directionOfRequest"
                className={inpClass(Boolean(errors.directionOfRequest))}
                rows={3}
                maxLength={MAX_REF}
                value={draft.directionOfRequest}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => {
                  setDraft((d) =>
                    d ? { ...d, directionOfRequest: sliceMax(e.target.value, MAX_REF) } : d,
                  );
                  clearErr('directionOfRequest');
                }}
              />
              <FieldError message={errors.directionOfRequest} />
            </div>

            <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
              <div className="ccr-fg">
                <label>Your Reference Number (External)</label>
                <input
                  id="ag-yourRef"
                  className={inpClass(Boolean(errors.yourReferenceNumber))}
                  value={draft.yourReferenceNumber}
                  readOnly={readOnly}
                  disabled={readOnly}
                  maxLength={MAX_REF}
                  onChange={(e) => {
                    setDraft((d) =>
                      d ? { ...d, yourReferenceNumber: sliceMax(e.target.value, MAX_REF) } : d,
                    );
                    clearErr('yourReferenceNumber');
                  }}
                />
                <FieldError message={errors.yourReferenceNumber} />
              </div>
              <div className="ccr-fg">
                <label>Other External Reference Number(s)</label>
                <input
                  className={inpClass(Boolean(errors.otherExternalReferences))}
                  value={draft.otherExternalReferences}
                  readOnly={readOnly}
                  disabled={readOnly}
                  maxLength={MAX_REF}
                  onChange={(e) =>
                    setDraft((d) =>
                      d ? { ...d, otherExternalReferences: sliceMax(e.target.value, MAX_REF) } : d,
                    )
                  }
                />
                <FieldError message={errors.otherExternalReferences} />
              </div>
            </div>

            <div className="ccr-fg" id="ag-previousAdvice" style={{ marginBottom: 14 }}>
              <label>Has previous advice been received?</label>
              <YesNoRadios
                name="ag-upd-previousAdvice"
                value={draft.hasPreviousAdvice}
                disabled={readOnly}
                onChange={(v) => {
                  setDraft((d) =>
                    d
                      ? {
                          ...d,
                          hasPreviousAdvice: v,
                          previousAdviceUploads: v === 'yes' ? d.previousAdviceUploads : [],
                        }
                      : d,
                  );
                  clearErr('hasPreviousAdvice');
                  clearErr('previousAdviceUploads');
                }}
              />
              <FieldError message={errors.hasPreviousAdvice} />
            </div>
            {draft.hasPreviousAdvice === 'yes' ? (
              <div className="ccr-fg" style={{ marginBottom: 14 }}>
                <label>{f.previousAdviceAttachment}</label>
                {readOnly ? (
                  <ReviewUploadedFilesTable
                    files={draft.previousAdviceUploads}
                    emptyMessage="—"
                  />
                ) : (
                  <WizardTempFileUploadList
                    files={draft.previousAdviceUploads}
                    uploadFile={attorneyGeneralService.uploadCaseFile}
                    error={errors.previousAdviceUploads}
                    onClearError={() => clearErr('previousAdviceUploads')}
                    onChange={(previousAdviceUploads: AttorneyGeneralInlineUpload[]) =>
                      setDraft((d) => (d ? { ...d, previousAdviceUploads } : d))
                    }
                    addButtonLabel={f.uploadDocuments}
                  />
                )}
              </div>
            ) : null}

            <div className="ccr-fg" id="ag-connectedAdviceCase" style={{ marginBottom: 14 }}>
              <label>Connected Advice Case?</label>
              <YesNoRadios
                name="ag-upd-connectedAdvice"
                value={draft.hasConnectedAdviceCase}
                disabled={readOnly}
                onChange={(v) => {
                  setDraft((d) =>
                    d
                      ? {
                          ...d,
                          hasConnectedAdviceCase: v,
                          connectedAdviceDetails: v === 'yes' ? d.connectedAdviceDetails : '',
                        }
                      : d,
                  );
                  clearErr('hasConnectedAdviceCase');
                  clearErr('connectedAdviceDetails');
                }}
              />
            </div>
            {draft.hasConnectedAdviceCase === 'yes' ? (
              <div className="ccr-fg" style={{ marginBottom: 14 }}>
                <label>
                  Connected Advice Case Details <span className="req">*</span>
                </label>
                <textarea
                  id="ag-connectedAdviceDetails"
                  className={inpClass(Boolean(errors.connectedAdviceDetails))}
                  rows={3}
                  maxLength={MAX_DESC}
                  value={draft.connectedAdviceDetails}
                  readOnly={readOnly}
                  disabled={readOnly}
                  onChange={(e) => {
                    setDraft((d) =>
                      d ? { ...d, connectedAdviceDetails: sliceMax(e.target.value, MAX_DESC) } : d,
                    );
                    clearErr('connectedAdviceDetails');
                  }}
                />
                <FieldError message={errors.connectedAdviceDetails} />
              </div>
            ) : null}

            <div className="ccr-fg" id="ag-actionInstituted" style={{ marginBottom: 14 }}>
              <label>Has action been instituted?</label>
              <YesNoRadios
                name="ag-upd-actionInstituted"
                value={draft.hasActionInstituted}
                disabled={readOnly}
                onChange={(v) => {
                  setDraft((d) =>
                    d
                      ? {
                          ...d,
                          hasActionInstituted: v,
                          ...(v === 'no'
                            ? {
                                courtId: '',
                                courtName: '',
                                courtTypeId: '',
                                courtTypeName: '',
                                courtTypeOther: '',
                              }
                            : {}),
                        }
                      : d,
                  );
                  clearErr('hasActionInstituted');
                  clearErr('courtTypeId');
                }}
              />
            </div>

            {showCourtType ? (
              <div className={showCourtName ? 'ccr-fr c2' : 'ccr-fg'} style={{ marginBottom: 14 }}>
                <div className="ccr-fg">
                  <label>
                    Court Type <span className="req">*</span>
                  </label>
                  <WizardSelect
                    id="ag-courtType"
                    hasError={Boolean(errors.courtTypeId)}
                    value={draft.courtTypeId}
                    disabled={readOnly}
                    placeholder="Select Court Type"
                    options={toSelectOpts(lookups.courtTypes)}
                    onChange={(id) => {
                      const name = lookupName(lookups.courtTypes, id);
                      setDraft((d) =>
                        d
                          ? {
                              ...d,
                              courtTypeId: id,
                              courtTypeName: name,
                              courtTypeOther: '',
                              courtId: '',
                              courtName: '',
                            }
                          : d,
                      );
                      clearErr('courtTypeId');
                    }}
                  />
                  <FieldError message={errors.courtTypeId} />
                </div>
                {showCourtName ? (
                  <div className="ccr-fg">
                    <label>Court Name</label>
                    <WizardSelect
                      id="ag-courtName"
                      value={draft.courtId}
                      disabled={readOnly || !draft.courtTypeId || lookups.courtsLoading}
                      placeholder="Select Court Name"
                      options={toSelectOpts(lookups.courts)}
                      onChange={(id) =>
                        setDraft((d) =>
                          d
                            ? { ...d, courtId: id, courtName: lookupName(lookups.courts, id) }
                            : d,
                        )
                      }
                    />
                  </div>
                ) : null}
              </div>
            ) : null}

            {showCourtOther ? (
              <div className="ccr-fg" style={{ marginBottom: 14 }}>
                <label>Court Type (Other)</label>
                <input
                  id="ag-courtTypeOther"
                  className={inpClass(Boolean(errors.courtTypeOther))}
                  maxLength={MAX_REF}
                  value={draft.courtTypeOther}
                  readOnly={readOnly}
                  disabled={readOnly}
                  onChange={(e) => {
                    setDraft((d) =>
                      d ? { ...d, courtTypeOther: sliceMax(e.target.value, MAX_REF) } : d,
                    );
                    clearErr('courtTypeOther');
                  }}
                />
                <FieldError message={errors.courtTypeOther} />
              </div>
            ) : null}

            <div className="ccr-fg" id="ag-appealMatter" style={{ marginBottom: 14 }}>
              <label>Is this an appeal matter?</label>
              <YesNoRadios
                name="ag-upd-appealMatter"
                value={draft.isAppealMatter}
                disabled={readOnly}
                onChange={(v) => {
                  setDraft((d) =>
                    d
                      ? {
                          ...d,
                          isAppealMatter: v,
                          ...(v === 'no'
                            ? { originalCourt: '', judgementOrderUploads: [] }
                            : {}),
                        }
                      : d,
                  );
                  clearErr('isAppealMatter');
                  clearErr('originalCourt');
                  clearErr('judgementOrderUploads');
                }}
              />
            </div>
            {draft.isAppealMatter === 'yes' ? (
              <>
                <div className="ccr-fg" style={{ marginBottom: 14 }}>
                  <label>
                    Original Court <span className="req">*</span>
                  </label>
                  <input
                    id="ag-originalCourt"
                    className={inpClass(Boolean(errors.originalCourt))}
                    maxLength={MAX_REF}
                    value={draft.originalCourt}
                    readOnly={readOnly}
                    disabled={readOnly}
                    onChange={(e) => {
                      setDraft((d) =>
                        d ? { ...d, originalCourt: sliceMax(e.target.value, MAX_REF) } : d,
                      );
                      clearErr('originalCourt');
                    }}
                  />
                  <FieldError message={errors.originalCourt} />
                </div>
                <div className="ccr-fg" style={{ marginBottom: 14 }}>
                  <label>{f.judgementOrder}</label>
                  {readOnly ? (
                    <ReviewUploadedFilesTable
                      files={draft.judgementOrderUploads}
                      emptyMessage="—"
                    />
                  ) : (
                    <WizardTempFileUploadList
                      files={draft.judgementOrderUploads}
                      uploadFile={attorneyGeneralService.uploadCaseFile}
                      error={errors.judgementOrderUploads}
                      onClearError={() => clearErr('judgementOrderUploads')}
                      onChange={(judgementOrderUploads: AttorneyGeneralInlineUpload[]) =>
                        setDraft((d) => (d ? { ...d, judgementOrderUploads } : d))
                      }
                      addButtonLabel={f.uploadDocuments}
                    />
                  )}
                </div>
              </>
            ) : null}

            <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
              <div className="ccr-fg">
                <label>Case Number</label>
                <input
                  id="ag-caseNumber"
                  className={inpClass(Boolean(errors.caseNumber))}
                  maxLength={MAX_REF}
                  value={draft.caseNumber}
                  readOnly={readOnly}
                  disabled={readOnly}
                  onChange={(e) => {
                    setDraft((d) =>
                      d ? { ...d, caseNumber: sliceMax(e.target.value, MAX_REF) } : d,
                    );
                    clearErr('caseNumber');
                  }}
                />
                <FieldError message={errors.caseNumber} />
              </div>
              <div className="ccr-fg" id="ag-connected">
                <label>Are there any connected matters (Cases)?</label>
                <YesNoRadios
                  name="ag-upd-connectedMatters"
                  value={draft.hasConnectedMatters}
                  disabled={readOnly}
                  onChange={(v) => {
                    setDraft((d) =>
                      d
                        ? {
                            ...d,
                            hasConnectedMatters: v,
                            connectedCaseNumber: v === 'yes' ? d.connectedCaseNumber : '',
                            connectedAgdFileRef: v === 'yes' ? d.connectedAgdFileRef : '',
                          }
                        : d,
                    );
                    clearErr('hasConnectedMatters');
                    clearErr('connectedCaseNumber');
                    clearErr('connectedAgdFileRef');
                  }}
                />
              </div>
            </div>
            {draft.hasConnectedMatters === 'yes' ? (
              <div className="ccr-fr c2">
                <div className="ccr-fg">
                  <label>
                    Connected Case Number <span className="req">*</span>
                  </label>
                  <input
                    id="ag-connectedCase"
                    className={inpClass(Boolean(errors.connectedCaseNumber))}
                    maxLength={MAX_REF}
                    value={draft.connectedCaseNumber}
                    readOnly={readOnly}
                    disabled={readOnly}
                    onChange={(e) => {
                      setDraft((d) =>
                        d ? { ...d, connectedCaseNumber: sliceMax(e.target.value, MAX_REF) } : d,
                      );
                      clearErr('connectedCaseNumber');
                    }}
                  />
                  <FieldError message={errors.connectedCaseNumber} />
                </div>
                <div className="ccr-fg">
                  <label>
                    Connected AGD File Reference Number <span className="req">*</span>
                  </label>
                  <input
                    id="ag-connectedAgd"
                    className={inpClass(Boolean(errors.connectedAgdFileRef))}
                    maxLength={MAX_REF}
                    value={draft.connectedAgdFileRef}
                    readOnly={readOnly}
                    disabled={readOnly}
                    onChange={(e) => {
                      setDraft((d) =>
                        d ? { ...d, connectedAgdFileRef: sliceMax(e.target.value, MAX_REF) } : d,
                      );
                      clearErr('connectedAgdFileRef');
                    }}
                  />
                  <FieldError message={errors.connectedAgdFileRef} />
                </div>
              </div>
            ) : null}
          </ReviewSubBlock>
          {sectionActions(1)}
        </ReviewSection>

        {/* Step 02 — Party Details */}
        <ReviewSection step="Step 02" title="Party Details" defaultOpen={false}>
          <div className="ccr-fg" id="ag-partyBodyType" style={{ marginBottom: 14 }}>
            <label>
              Party Body Type <span className="req">*</span>
            </label>
            <div className="ccr-radio-row" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
              <label className="ccr-radio">
                <input
                  type="radio"
                  name="ag-upd-partyBodyType"
                  checked={draft.partyBodyType === 'government'}
                  disabled={readOnly}
                  onChange={() => {
                    setDraft((d) => (d ? { ...d, partyBodyType: 'government' } : d));
                    clearErr('partyBodyType');
                  }}
                />
                Government Ministry/ Department/Other Unincorporated Body
              </label>
              <label className="ccr-radio">
                <input
                  type="radio"
                  name="ag-upd-partyBodyType"
                  checked={draft.partyBodyType === 'incorporated'}
                  disabled={readOnly}
                  onChange={() => {
                    setDraft((d) => (d ? { ...d, partyBodyType: 'incorporated' } : d));
                    clearErr('partyBodyType');
                  }}
                />
                Public Corporation/Statutory Board/State Owned Entity/Other Incorporated Body
              </label>
            </div>
            <FieldError message={errors.partyBodyType} />
          </div>
          <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
            <div className="ccr-fg">
              <label>Plaintiff / Applicant Name</label>
              <input
                id="ag-plaintiff"
                className={inpClass(Boolean(errors.plaintiffName))}
                maxLength={MAX_REF}
                value={draft.plaintiffName}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => {
                  setDraft((d) =>
                    d ? { ...d, plaintiffName: sliceMax(e.target.value, MAX_REF) } : d,
                  );
                  clearErr('plaintiffName');
                }}
              />
              <FieldError message={errors.plaintiffName} />
            </div>
            <div className="ccr-fg">
              <label>Defendant / Respondent Name</label>
              <input
                id="ag-defendant"
                className={inpClass(Boolean(errors.defendantName))}
                maxLength={MAX_REF}
                value={draft.defendantName}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => {
                  setDraft((d) =>
                    d ? { ...d, defendantName: sliceMax(e.target.value, MAX_REF) } : d,
                  );
                  clearErr('defendantName');
                }}
              />
              <FieldError message={errors.defendantName} />
            </div>
          </div>
          <div className="ccr-fg" style={{ marginBottom: 14 }}>
            <label>Other Involved Institutions</label>
            <textarea
              className={inpClass(Boolean(errors.otherInvolvedInstitutions))}
              rows={3}
              maxLength={MAX_DESC}
              value={draft.otherInvolvedInstitutions}
              readOnly={readOnly}
              disabled={readOnly}
              onChange={(e) =>
                setDraft((d) =>
                  d ? { ...d, otherInvolvedInstitutions: sliceMax(e.target.value, MAX_DESC) } : d,
                )
              }
            />
            <FieldError message={errors.otherInvolvedInstitutions} />
          </div>
          <div className="ccr-fr c2">
            <div className="ccr-fg">
              <label>Name of the Legal Officer/ Officer In-Charge</label>
              <input
                className={inpClass(Boolean(errors.legalOfficerName))}
                maxLength={MAX_REF}
                value={draft.legalOfficerName}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => {
                  setDraft((d) =>
                    d ? { ...d, legalOfficerName: sliceMax(e.target.value, MAX_REF) } : d,
                  );
                  clearErr('legalOfficerName');
                }}
              />
              <FieldError message={errors.legalOfficerName} />
            </div>
            <div className="ccr-fg">
              <label>Contact Number</label>
              <input
                className={inpClass(Boolean(errors.legalOfficerContact))}
                inputMode="numeric"
                value={draft.legalOfficerContact}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, '').slice(0, MAX_CONTACT);
                  setDraft((d) => (d ? { ...d, legalOfficerContact: digits } : d));
                  const err = contactNumberError(digits);
                  setErrors((prev) => ({ ...prev, legalOfficerContact: err ?? '' }));
                }}
              />
              <FieldError message={errors.legalOfficerContact} />
            </div>
          </div>
          {sectionActions(2)}
        </ReviewSection>

        {/* Step 03 — Incident Details */}
        <ReviewSection step="Step 03" title="Incident Details" defaultOpen={false}>
          <div className="ccr-fg" style={{ marginBottom: 14 }}>
            <label>Subject of the Dispute</label>
            <textarea
              id="ag-subject"
              className={inpClass(Boolean(errors.subjectOfDispute))}
              rows={4}
              maxLength={MAX_DESC}
              value={draft.subjectOfDispute}
              readOnly={readOnly}
              disabled={readOnly}
              onChange={(e) => {
                setDraft((d) =>
                  d ? { ...d, subjectOfDispute: sliceMax(e.target.value, MAX_DESC) } : d,
                );
                clearErr('subjectOfDispute');
              }}
            />
            <FieldError message={errors.subjectOfDispute} />
          </div>
          <div className="ccr-fg" style={{ marginBottom: 14 }}>
            <label>Incident Occurred Date</label>
            <FormattedDateRangeInput
              from={draft.incidentDateFrom}
              to={draft.incidentDateTo}
              enableYmdViews
              disabled={readOnly}
              onChange={(from, to) =>
                setDraft((d) => (d ? { ...d, incidentDateFrom: from, incidentDateTo: to } : d))
              }
            />
          </div>
          <div className="ccr-fr c2" style={{ marginBottom: showNextStepOther ? 14 : 0 }}>
            <div className="ccr-fg">
              <label>Next Step of the Case</label>
              <WizardSelect
                value={draft.nextStepOfCaseId}
                disabled={readOnly}
                placeholder="Select Next Step"
                options={toSelectOpts(lookups.nextSteps)}
                onChange={(id) => {
                  const row = lookups.nextSteps.find((s) => s.id === id);
                  const name = row?.name ?? '';
                  const otherSelected = isOtherNextStep(name, row?.code ?? id);
                  setDraft((d) =>
                    d
                      ? {
                          ...d,
                          nextStepOfCaseId: id,
                          nextStepOfCaseName: name,
                          nextStepOfCaseOther: otherSelected ? d.nextStepOfCaseOther : '',
                        }
                      : d,
                  );
                  if (!otherSelected) clearErr('nextStepOfCaseOther');
                }}
              />
            </div>
            <div className="ccr-fg">
              <label>Next Date of the Case</label>
              <FormattedDateInput
                id="ag-nextCaseDate"
                className={inpClass(Boolean(errors.nextCaseDate))}
                value={draft.nextCaseDate}
                minDate={minDeadlineDate}
                enableYmdViews
                disabled={readOnly}
                onChange={(next) => {
                  setDraft((d) => (d ? { ...d, nextCaseDate: next } : d));
                  clearErr('nextCaseDate');
                }}
              />
              <FieldError message={errors.nextCaseDate} />
            </div>
          </div>
          {showNextStepOther ? (
            <div className="ccr-fg" style={{ marginBottom: 14 }}>
              <label>Next Step (Other)</label>
              <input
                id="ag-nextStepOther"
                className={inpClass(Boolean(errors.nextStepOfCaseOther))}
                maxLength={MAX_REF}
                value={draft.nextStepOfCaseOther}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => {
                  setDraft((d) =>
                    d ? { ...d, nextStepOfCaseOther: sliceMax(e.target.value, MAX_REF) } : d,
                  );
                  clearErr('nextStepOfCaseOther');
                }}
              />
              <FieldError message={errors.nextStepOfCaseOther} />
            </div>
          ) : null}
          {sectionActions(3)}
        </ReviewSection>

        {/* Step 04 — Victim Details */}
        <ReviewSection step="Step 04" title="Victim Details" defaultOpen={false}>
          <div className="ccr-fr c2">
            <div className="ccr-fg">
              <label>Name of the Victim</label>
              <input
                className={inpClass(Boolean(errors.victimName))}
                maxLength={MAX_REF}
                value={draft.victimName}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => {
                  setDraft((d) =>
                    d ? { ...d, victimName: sliceMax(e.target.value, MAX_REF) } : d,
                  );
                  clearErr('victimName');
                }}
              />
              <FieldError message={errors.victimName} />
            </div>
            <div className="ccr-fg">
              <label>Date of Birth</label>
              <FormattedDateInput
                className={inpClass(false)}
                value={draft.victimDob}
                maxDate={maxDob}
                enableYmdViews
                disabled={readOnly}
                onChange={(next) => setDraft((d) => (d ? { ...d, victimDob: next } : d))}
              />
            </div>
            <div className="ccr-fg">
              <label>Gender</label>
              <WizardSelect
                value={draft.victimGenderId}
                disabled={readOnly}
                placeholder="Select Gender"
                options={toSelectOpts(lookups.genders)}
                onChange={(id) =>
                  setDraft((d) =>
                    d
                      ? {
                          ...d,
                          victimGenderId: id,
                          victimGenderName: lookupName(lookups.genders, id),
                        }
                      : d,
                  )
                }
              />
            </div>
          </div>
          {sectionActions(4)}
        </ReviewSection>

        {/* Step 05 — Offence & Accused */}
        <ReviewSection step="Step 05" title="Offence & Accused Details" defaultOpen={false}>
          <div className="ccr-fg" style={{ marginBottom: 14 }}>
            <label>Date of Offence</label>
            <FormattedDateRangeInput
              id="ag-offenceFrom"
              className={inpClass(Boolean(errors.offenceDateFrom))}
              from={draft.offenceDateFrom}
              to={draft.offenceDateTo}
              maxDate={maxOffenceDate}
              enableYmdViews
              disabled={readOnly}
              onChange={(from, to) => {
                setDraft((d) => (d ? { ...d, offenceDateFrom: from, offenceDateTo: to } : d));
                clearErr('offenceDateFrom');
              }}
            />
            <FieldError message={errors.offenceDateFrom} />
          </div>
          <div className="ccr-fg" style={{ marginBottom: 14 }}>
            <label>Place of Offence</label>
            <textarea
              id="ag-placeOfOffence"
              className={inpClass(Boolean(errors.placeOfOffence))}
              rows={3}
              maxLength={MAX_DESC}
              value={draft.placeOfOffence}
              readOnly={readOnly}
              disabled={readOnly}
              onChange={(e) => {
                setDraft((d) =>
                  d ? { ...d, placeOfOffence: sliceMax(e.target.value, MAX_DESC) } : d,
                );
                clearErr('placeOfOffence');
              }}
            />
            <FieldError message={errors.placeOfOffence} />
          </div>
          <div className="ccr-accused-panel">
            <div className="ccr-accused-panel-hdr">Accused / Suspects</div>
            <div className="ccr-accused-panel-body">
              {draft.accused.map((a, i) => (
                <div key={a.id} style={{ marginBottom: i < draft.accused.length - 1 ? 12 : 0 }}>
                  <div className="ccr-accused-row">
                    <div className="ccr-fg">
                      <label>Name</label>
                      <input
                        id={`ag-accused-name-${i}`}
                        className={inpClass(Boolean(errors[`accused-name-${i}`]))}
                        maxLength={MAX_DESC}
                        value={a.name}
                        readOnly={readOnly}
                        disabled={readOnly}
                        onChange={(e) => {
                          updateAccused(a.id, { name: sliceMax(e.target.value, MAX_DESC) });
                          clearErr(`accused-name-${i}`);
                        }}
                      />
                      <FieldError message={errors[`accused-name-${i}`]} />
                    </div>
                    <div className="ccr-fg">
                      <label>Status</label>
                      <WizardSelect
                        id={`ag-accused-status-${i}`}
                        hasError={Boolean(errors[`accused-status-${i}`])}
                        value={a.status}
                        disabled={readOnly}
                        placeholder="Select Status"
                        options={toSelectOpts(lookups.suspectStatuses)}
                        onChange={(id) => updateAccused(a.id, { status: id, statusOther: '' })}
                      />
                      <FieldError message={errors[`accused-status-${i}`]} />
                    </div>
                    {!readOnly ? (
                      <button
                        type="button"
                        className="ccr-accused-del"
                        aria-label={`Remove accused ${i + 1}`}
                        disabled={draft.accused.length <= 1}
                        onClick={() =>
                          setDraft((d) =>
                            d
                              ? {
                                  ...d,
                                  accused:
                                    d.accused.length > 1
                                      ? d.accused.filter((x) => x.id !== a.id)
                                      : d.accused,
                                }
                              : d,
                          )
                        }
                      >
                        <TrashIcon />
                      </button>
                    ) : null}
                  </div>
                  {isStatusOther(a.status, lookups.suspectStatuses) ? (
                    <div className="ccr-accused-other ccr-fg">
                      <label>
                        Status (Other) <span className="req">*</span>
                      </label>
                      <input
                        id={`ag-accused-statusOther-${i}`}
                        className={inpClass(Boolean(errors[`accused-statusOther-${i}`]))}
                        maxLength={MAX_REF}
                        value={a.statusOther}
                        readOnly={readOnly}
                        disabled={readOnly}
                        onChange={(e) => {
                          updateAccused(a.id, { statusOther: sliceMax(e.target.value, MAX_REF) });
                          clearErr(`accused-statusOther-${i}`);
                        }}
                      />
                      <FieldError message={errors[`accused-statusOther-${i}`]} />
                    </div>
                  ) : null}
                </div>
              ))}
              {!readOnly ? (
                <button
                  type="button"
                  className="ccr-btn-ol"
                  style={{ marginTop: 10 }}
                  onClick={() =>
                    setDraft((d) =>
                      d ? { ...d, accused: [...d.accused, newAttorneyGeneralAccused()] } : d,
                    )
                  }
                >
                  Add Accused / Suspect
                </button>
              ) : null}
            </div>
          </div>
          {sectionActions(5)}
        </ReviewSection>

        {/* Step 06 — Accused Person Details (Extradition & MLA) */}
        <ReviewSection
          step="Step 06"
          title="Accused Person Details - Extradition & MLA"
          defaultOpen={false}
        >
          <div className="ccr-fr c2">
            <div className="ccr-fg" style={{ gridColumn: '1 / -1' }}>
              <label>Name of the Accused</label>
              <input
                id="ag-accusedName"
                className={inpClass(Boolean(errors.accusedName))}
                maxLength={MAX_REF}
                value={draft.accusedName}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => {
                  setDraft((d) =>
                    d ? { ...d, accusedName: sliceMax(e.target.value, MAX_REF) } : d,
                  );
                  clearErr('accusedName');
                }}
              />
              <FieldError message={errors.accusedName} />
            </div>
            <div className="ccr-fg">
              <label>Nationality</label>
              <WizardSelect
                id="ag-nationality"
                value={draft.nationalityId}
                disabled={readOnly}
                placeholder="Select Nationality"
                options={toSelectOpts(
                  mergeLookupItems(lookups.nationalities, draft.nationalityId, draft.nationalityName),
                )}
                onChange={(id) =>
                  setDraft((d) =>
                    d
                      ? {
                          ...d,
                          nationalityId: id,
                          nationalityName: lookupName(lookups.nationalities, id),
                        }
                      : d,
                  )
                }
              />
            </div>
            <div className="ccr-fg">
              <label>Country of Origin</label>
              <WizardSelect
                value={draft.countryOfOriginId}
                disabled={readOnly}
                placeholder="Select Country"
                options={toSelectOpts(
                  mergeLookupItems(
                    lookups.countries,
                    draft.countryOfOriginId,
                    draft.countryOfOriginName,
                  ),
                )}
                onChange={(id) =>
                  setDraft((d) =>
                    d
                      ? {
                          ...d,
                          countryOfOriginId: id,
                          countryOfOriginName: lookupName(lookups.countries, id),
                        }
                      : d,
                  )
                }
              />
            </div>
            <div className="ccr-fg">
              <label>Date of Birth</label>
              <FormattedDateInput
                value={draft.accusedDob}
                maxDate={maxDob}
                enableYmdViews
                disabled={readOnly}
                onChange={(next) => setDraft((d) => (d ? { ...d, accusedDob: next } : d))}
              />
              {accusedAge != null ? (
                <p className="tcd-update-ts" style={{ marginTop: 4 }}>
                  Age: {accusedAge}
                </p>
              ) : null}
            </div>
            <div className="ccr-fg">
              <label>Gender</label>
              <WizardSelect
                value={draft.accusedGenderId}
                disabled={readOnly}
                placeholder="Select Gender"
                options={toSelectOpts(lookups.genders)}
                onChange={(id) =>
                  setDraft((d) =>
                    d
                      ? {
                          ...d,
                          accusedGenderId: id,
                          accusedGenderName: lookupName(lookups.genders, id),
                        }
                      : d,
                  )
                }
              />
            </div>
            <div className="ccr-fg">
              <label>Passport/ID Number</label>
              <input
                className={inpClass(Boolean(errors.passportOrIdNumber))}
                maxLength={MAX_REF}
                value={draft.passportOrIdNumber}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) =>
                  setDraft((d) =>
                    d ? { ...d, passportOrIdNumber: sliceMax(e.target.value, MAX_REF) } : d,
                  )
                }
              />
              <FieldError message={errors.passportOrIdNumber} />
            </div>
            <div className="ccr-fg">
              <label>Current Custody Status</label>
              <WizardSelect
                value={draft.custodyStatusId}
                disabled={readOnly}
                placeholder="Select Custody Status"
                options={toSelectOpts(custodyOptions)}
                onChange={(id) => {
                  const name = lookupName(custodyOptions, id);
                  setDraft((d) =>
                    d
                      ? {
                          ...d,
                          custodyStatusId: id,
                          custodyStatusName: name,
                          custodialAuthority: requiresCustodialAuthority(
                            name,
                            custodyOptions.find((s) => s.id === id)?.code,
                          )
                            ? d.custodialAuthority
                            : '',
                        }
                      : d,
                  );
                }}
              />
            </div>
            {showCustodialAuthority ? (
              <div className="ccr-fg">
                <label>Custodial Authority</label>
                <input
                  className={inpClass(Boolean(errors.custodialAuthority))}
                  maxLength={MAX_REF}
                  value={draft.custodialAuthority}
                  readOnly={readOnly}
                  disabled={readOnly}
                  onChange={(e) =>
                    setDraft((d) =>
                      d ? { ...d, custodialAuthority: sliceMax(e.target.value, MAX_REF) } : d,
                    )
                  }
                />
                <FieldError message={errors.custodialAuthority} />
              </div>
            ) : null}
            <div className="ccr-fg" style={{ gridColumn: '1 / -1' }}>
              <label>Last Known Location</label>
              <textarea
                className={inpClass(Boolean(errors.lastKnownLocation))}
                rows={2}
                maxLength={MAX_DESC}
                value={draft.lastKnownLocation}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) =>
                  setDraft((d) =>
                    d ? { ...d, lastKnownLocation: sliceMax(e.target.value, MAX_DESC) } : d,
                  )
                }
              />
              <FieldError message={errors.lastKnownLocation} />
            </div>
          </div>
          {sectionActions(6)}
        </ReviewSection>

        {/* Step 07 — Legal & Extradition */}
        <ReviewSection step="Step 07" title="Legal & Extradition" defaultOpen={false}>
          <div className="ccr-fg" style={{ marginBottom: 14 }}>
            <label>Arrest Warrant Available</label>
            <YesNoRadios
              name="ag-upd-arrestWarrant"
              value={draft.arrestWarrantAvailable}
              disabled={readOnly}
              onChange={(v) => {
                setDraft((d) =>
                  d
                    ? {
                        ...d,
                        arrestWarrantAvailable: v,
                        warrantReference: v === 'yes' ? d.warrantReference : '',
                      }
                    : d,
                );
                clearErr('warrantReference');
              }}
            />
          </div>
          {draft.arrestWarrantAvailable === 'yes' ? (
            <div className="ccr-fg" style={{ marginBottom: 14 }}>
              <label>
                Warrant Reference <span className="req">*</span>
              </label>
              <input
                id="ag-warrantReference"
                className={inpClass(Boolean(errors.warrantReference))}
                maxLength={MAX_REF}
                value={draft.warrantReference}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => {
                  setDraft((d) =>
                    d ? { ...d, warrantReference: sliceMax(e.target.value, MAX_REF) } : d,
                  );
                  clearErr('warrantReference');
                }}
              />
              <FieldError message={errors.warrantReference} />
            </div>
          ) : null}
          <div className="ccr-fg">
            <label>Applicable Treaty/Arrangement</label>
            <input
              className={inpClass(Boolean(errors.applicableTreatyArrangement))}
              maxLength={MAX_REF}
              value={draft.applicableTreatyArrangement}
              readOnly={readOnly}
              disabled={readOnly}
              onChange={(e) =>
                setDraft((d) =>
                  d
                    ? { ...d, applicableTreatyArrangement: sliceMax(e.target.value, MAX_REF) }
                    : d,
                )
              }
            />
            <FieldError message={errors.applicableTreatyArrangement} />
          </div>
          {sectionActions(7)}
        </ReviewSection>

        {/* Step 08 — Petitioner Details */}
        <ReviewSection step="Step 08" title="Petitioner Details" defaultOpen={false}>
          <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
            <div className="ccr-fg">
              <label>Petitioner Name</label>
              <input
                id="ag-petitionerName"
                className={inpClass(Boolean(errors.petitionerName))}
                maxLength={MAX_REF}
                value={draft.petitionerName}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => {
                  setDraft((d) =>
                    d ? { ...d, petitionerName: sliceMax(e.target.value, MAX_REF) } : d,
                  );
                  clearErr('petitionerName');
                }}
              />
              <FieldError message={errors.petitionerName} />
            </div>
            <div className="ccr-fg">
              <label>NIC</label>
              <input
                id="ag-petitionerNic"
                className={inpClass(Boolean(errors.petitionerNic))}
                maxLength={MAX_REF}
                value={draft.petitionerNic}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => {
                  setDraft((d) =>
                    d ? { ...d, petitionerNic: sliceMax(e.target.value, MAX_REF) } : d,
                  );
                  clearErr('petitionerNic');
                }}
              />
              <FieldError message={errors.petitionerNic} />
            </div>
          </div>
          <div className="ccr-fg" style={{ marginBottom: 14 }}>
            <label>Address</label>
            <textarea
              className={inpClass(Boolean(errors.petitionerAddress))}
              rows={3}
              maxLength={MAX_DESC}
              value={draft.petitionerAddress}
              readOnly={readOnly}
              disabled={readOnly}
              onChange={(e) =>
                setDraft((d) =>
                  d ? { ...d, petitionerAddress: sliceMax(e.target.value, MAX_DESC) } : d,
                )
              }
            />
            <FieldError message={errors.petitionerAddress} />
          </div>
          <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
            <div className="ccr-fg">
              <label>Contact Number</label>
              <input
                className={inpClass(Boolean(errors.petitionerContact))}
                inputMode="numeric"
                value={draft.petitionerContact}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, '').slice(0, MAX_CONTACT);
                  setDraft((d) => (d ? { ...d, petitionerContact: digits } : d));
                  const err = contactNumberError(digits);
                  setErrors((prev) => ({ ...prev, petitionerContact: err ?? '' }));
                }}
              />
              <FieldError message={errors.petitionerContact} />
            </div>
            <div className="ccr-fg">
              <label>Email Address</label>
              <input
                className={inpClass(Boolean(errors.petitionerEmail))}
                type="email"
                maxLength={MAX_REF}
                value={draft.petitionerEmail}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => {
                  setDraft((d) =>
                    d ? { ...d, petitionerEmail: sliceMax(e.target.value, MAX_REF) } : d,
                  );
                  clearErr('petitionerEmail');
                }}
              />
              <FieldError message={errors.petitionerEmail} />
            </div>
          </div>
          <div className="ccr-fg" style={{ marginBottom: 14 }}>
            <label>Represented by Attorney</label>
            <YesNoRadios
              name="ag-upd-representedByAttorney"
              value={draft.representedByAttorney || 'no'}
              disabled={readOnly}
              onChange={(v) => {
                setDraft((d) =>
                  d
                    ? {
                        ...d,
                        representedByAttorney: v,
                        attorneyName: v === 'yes' ? d.attorneyName : '',
                        attorneyContact: v === 'yes' ? d.attorneyContact : '',
                      }
                    : d,
                );
              }}
            />
          </div>
          {draft.representedByAttorney === 'yes' ? (
            <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
              <div className="ccr-fg">
                <label>Attorney Name</label>
                <input
                  className={inpClass(Boolean(errors.attorneyName))}
                  maxLength={MAX_REF}
                  value={draft.attorneyName}
                  readOnly={readOnly}
                  disabled={readOnly}
                  onChange={(e) =>
                    setDraft((d) =>
                      d ? { ...d, attorneyName: sliceMax(e.target.value, MAX_REF) } : d,
                    )
                  }
                />
                <FieldError message={errors.attorneyName} />
              </div>
              <div className="ccr-fg">
                <label>Attorney Contact</label>
                <input
                  className={inpClass(Boolean(errors.attorneyContact))}
                  inputMode="numeric"
                  value={draft.attorneyContact}
                  readOnly={readOnly}
                  disabled={readOnly}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, '').slice(0, MAX_CONTACT);
                    setDraft((d) => (d ? { ...d, attorneyContact: digits } : d));
                    const err = contactNumberError(digits);
                    setErrors((prev) => ({ ...prev, attorneyContact: err ?? '' }));
                  }}
                />
                <FieldError message={errors.attorneyContact} />
              </div>
            </div>
          ) : null}
          <div className="ccr-fr c2">
            <div className="ccr-fg">
              <label>Respondent Name(s)</label>
              <input
                className={inpClass(Boolean(errors.respondentNames))}
                maxLength={MAX_REF}
                value={draft.respondentNames}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) =>
                  setDraft((d) =>
                    d ? { ...d, respondentNames: sliceMax(e.target.value, MAX_REF) } : d,
                  )
                }
              />
              <FieldError message={errors.respondentNames} />
            </div>
            <div className="ccr-fg">
              <label>Respondent Type</label>
              <WizardSelect
                value={draft.respondentType}
                disabled={readOnly}
                placeholder="Select Respondent Type"
                options={toSelectOpts(
                  mergeLookupItems(
                    lookups.respondentTypes,
                    draft.respondentType,
                    draft.respondentTypeName,
                  ),
                )}
                onChange={(id) =>
                  setDraft((d) =>
                    d
                      ? {
                          ...d,
                          respondentType: id,
                          respondentTypeName: lookupName(lookups.respondentTypes, id),
                        }
                      : d,
                  )
                }
              />
            </div>
            <div className="ccr-fg" style={{ gridColumn: '1 / -1' }}>
              <label>Respondent Address</label>
              <textarea
                className={inpClass(Boolean(errors.respondentAddress))}
                rows={2}
                maxLength={MAX_DESC}
                value={draft.respondentAddress}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) =>
                  setDraft((d) =>
                    d ? { ...d, respondentAddress: sliceMax(e.target.value, MAX_DESC) } : d,
                  )
                }
              />
              <FieldError message={errors.respondentAddress} />
            </div>
            <div className="ccr-fg">
              <label>Number of Respondents</label>
              <input
                id="ag-numberOfRespondents"
                className={inpClass(Boolean(errors.numberOfRespondents))}
                inputMode="numeric"
                value={draft.numberOfRespondents}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => {
                  setDraft((d) =>
                    d
                      ? {
                          ...d,
                          numberOfRespondents: onlyDigitsMax(
                            e.target.value,
                            MAX_NUMBER_OF_RESPONDENTS_DIGITS,
                          ),
                        }
                      : d,
                  );
                  clearErr('numberOfRespondents');
                }}
              />
              <FieldError message={errors.numberOfRespondents} />
            </div>
          </div>
          {sectionActions(8)}
        </ReviewSection>

        {/* Step 09 — Right to Information */}
        <ReviewSection step="Step 09" title="Right to Information" defaultOpen={false}>
          <ReviewSubBlock title="Requester Details">
            <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
              <div className="ccr-fg">
                <label>Name of Requestor</label>
                <input
                  id="ag-rti-requestorName"
                  className={inpClass(Boolean(errors.rtiRequestorName))}
                  maxLength={MAX_DESC}
                  value={draft.rtiRequestorName}
                  readOnly={readOnly}
                  disabled={readOnly}
                  onChange={(e) => {
                    setDraft((d) =>
                      d ? { ...d, rtiRequestorName: sliceMax(e.target.value, MAX_DESC) } : d,
                    );
                    clearErr('rtiRequestorName');
                  }}
                />
                <FieldError message={errors.rtiRequestorName} />
              </div>
              <div className="ccr-fg">
                <label>NIC/ Passport Number</label>
                <input
                  className={inpClass(Boolean(errors.rtiNicOrPassport))}
                  maxLength={MAX_REF}
                  value={draft.rtiNicOrPassport}
                  readOnly={readOnly}
                  disabled={readOnly}
                  onChange={(e) =>
                    setDraft((d) =>
                      d ? { ...d, rtiNicOrPassport: sliceMax(e.target.value, MAX_REF) } : d,
                    )
                  }
                />
                <FieldError message={errors.rtiNicOrPassport} />
              </div>
            </div>
            <div className="ccr-fg" style={{ marginBottom: 14 }}>
              <label>Address</label>
              <textarea
                className={inpClass(Boolean(errors.rtiAddress))}
                rows={3}
                maxLength={MAX_DESC}
                value={draft.rtiAddress}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) =>
                  setDraft((d) =>
                    d ? { ...d, rtiAddress: sliceMax(e.target.value, MAX_DESC) } : d,
                  )
                }
              />
              <FieldError message={errors.rtiAddress} />
            </div>
            <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
              <div className="ccr-fg">
                <label>Contact Number</label>
                <input
                  className={inpClass(Boolean(errors.rtiContactNumber))}
                  inputMode="numeric"
                  value={draft.rtiContactNumber}
                  readOnly={readOnly}
                  disabled={readOnly}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, '').slice(0, MAX_CONTACT);
                    setDraft((d) => (d ? { ...d, rtiContactNumber: digits } : d));
                    const err = contactNumberError(digits);
                    setErrors((prev) => ({ ...prev, rtiContactNumber: err ?? '' }));
                  }}
                />
                <FieldError message={errors.rtiContactNumber} />
              </div>
              <div className="ccr-fg">
                <label>Email Address</label>
                <input
                  className={inpClass(Boolean(errors.rtiEmailAddress))}
                  type="email"
                  maxLength={MAX_REF}
                  value={draft.rtiEmailAddress}
                  readOnly={readOnly}
                  disabled={readOnly}
                  onChange={(e) => {
                    setDraft((d) =>
                      d ? { ...d, rtiEmailAddress: sliceMax(e.target.value, MAX_REF) } : d,
                    );
                    clearErr('rtiEmailAddress');
                  }}
                />
                <FieldError message={errors.rtiEmailAddress} />
              </div>
            </div>
            <div className="ccr-fg">
              <label>Citizen of Sri Lanka</label>
              <YesNoRadios
                name="ag-upd-rtiCitizen"
                value={draft.rtiIsSriLankanCitizen}
                disabled={readOnly}
                onChange={(v) => setDraft((d) => (d ? { ...d, rtiIsSriLankanCitizen: v } : d))}
              />
            </div>
          </ReviewSubBlock>

          <ReviewSubBlock title="Information Request Details">
            <div className="ccr-fg" style={{ marginBottom: 14 }}>
              <label>Specific Public Authority Name</label>
              <input
                className={inpClass(Boolean(errors.rtiSpecificPublicAuthorityName))}
                maxLength={MAX_REF}
                value={draft.rtiSpecificPublicAuthorityName}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) =>
                  setDraft((d) =>
                    d
                      ? {
                          ...d,
                          rtiSpecificPublicAuthorityName: sliceMax(e.target.value, MAX_REF),
                        }
                      : d,
                  )
                }
              />
              <FieldError message={errors.rtiSpecificPublicAuthorityName} />
            </div>
            <div className="ccr-fg" style={{ marginBottom: 14 }}>
              <label>Requested Information Details</label>
              <textarea
                className={inpClass(Boolean(errors.rtiRequestedInformationDetails))}
                rows={4}
                maxLength={MAX_DESC}
                value={draft.rtiRequestedInformationDetails}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) =>
                  setDraft((d) =>
                    d
                      ? {
                          ...d,
                          rtiRequestedInformationDetails: sliceMax(e.target.value, MAX_DESC),
                        }
                      : d,
                  )
                }
              />
              <FieldError message={errors.rtiRequestedInformationDetails} />
            </div>
            <div className="ccr-fg">
              <label>Information Requested Time Period</label>
              <input
                className={inpClass(Boolean(errors.rtiInformationRequestedTimePeriod))}
                maxLength={MAX_REF}
                value={draft.rtiInformationRequestedTimePeriod}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) =>
                  setDraft((d) =>
                    d
                      ? {
                          ...d,
                          rtiInformationRequestedTimePeriod: sliceMax(e.target.value, MAX_REF),
                        }
                      : d,
                  )
                }
              />
              <FieldError message={errors.rtiInformationRequestedTimePeriod} />
            </div>
          </ReviewSubBlock>

          <ReviewSubBlock title="Additional Information">
            <div id="ag-rti-preferredAccess" style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 13, fontWeight: 700, display: 'block', marginBottom: 10 }}>
                Preferred Method of Access
              </label>
              <div className="ccr-check-grid">
                {lookups.preferredMethodOfAccess.map((m) => (
                  <label key={m.id} className="ccr-check">
                    <input
                      type="checkbox"
                      checked={draft.rtiPreferredMethodOfAccessIds.includes(m.id)}
                      disabled={readOnly}
                      onChange={() => togglePreferredAccess(m.id)}
                    />
                    <span>{translatePreferredAccessName(m.name, t)}</span>
                  </label>
                ))}
              </div>
              <FieldError message={errors.rtiPreferredMethodOfAccessIds} />
            </div>
            <div className="ccr-fg" style={{ marginBottom: 14 }}>
              <label>Preferred Language for Response</label>
              <WizardSelect
                id="ag-rti-preferredLanguage"
                value={draft.rtiPreferredLanguageId}
                disabled={readOnly}
                placeholder="Select Language"
                options={toSelectOpts(lookups.preferredLanguages)}
                onChange={(id) => {
                  setDraft((d) =>
                    d
                      ? {
                          ...d,
                          rtiPreferredLanguageId: id,
                          rtiPreferredLanguageName: lookupName(lookups.preferredLanguages, id),
                        }
                      : d,
                  );
                  clearErr('rtiPreferredLanguageId');
                }}
              />
            </div>
            <div className="ccr-fg" style={{ marginBottom: 14 }}>
              <label>Involves Life or Personal Liberty</label>
              <YesNoRadios
                name="ag-upd-rtiLifeLiberty"
                value={draft.rtiInvolvesLifeOrPersonalLiberty}
                disabled={readOnly}
                onChange={(v) => {
                  setDraft((d) =>
                    d
                      ? {
                          ...d,
                          rtiInvolvesLifeOrPersonalLiberty: v,
                          rtiJustification: v === 'yes' ? d.rtiJustification : '',
                        }
                      : d,
                  );
                  clearErr('rtiJustification');
                }}
              />
            </div>
            {draft.rtiInvolvesLifeOrPersonalLiberty === 'yes' ? (
              <div className="ccr-fg" style={{ marginBottom: 14 }}>
                <label>Justification</label>
                <textarea
                  className={inpClass(Boolean(errors.rtiJustification))}
                  rows={3}
                  maxLength={MAX_DESC}
                  value={draft.rtiJustification}
                  readOnly={readOnly}
                  disabled={readOnly}
                  onChange={(e) => {
                    setDraft((d) =>
                      d ? { ...d, rtiJustification: sliceMax(e.target.value, MAX_DESC) } : d,
                    );
                    clearErr('rtiJustification');
                  }}
                />
                <FieldError message={errors.rtiJustification} />
              </div>
            ) : null}
            <div className="ccr-fg">
              <label>Additional Notes</label>
              <textarea
                className={inpClass(Boolean(errors.rtiAdditionalNotes))}
                rows={3}
                maxLength={MAX_DESC}
                value={draft.rtiAdditionalNotes}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) =>
                  setDraft((d) =>
                    d ? { ...d, rtiAdditionalNotes: sliceMax(e.target.value, MAX_DESC) } : d,
                  )
                }
              />
              <FieldError message={errors.rtiAdditionalNotes} />
            </div>
          </ReviewSubBlock>
          {sectionActions(9)}
        </ReviewSection>

        {/* Step 10 — Timeline */}
        <ReviewSection step="Step 10" title="Timeline" defaultOpen={false}>
          <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
            <div className="ccr-fg">
              <label>Urgency Level</label>
              <WizardSelect
                id="ag-urgency"
                hasError={Boolean(errors.urgencyLevelId)}
                value={draft.urgencyLevelId}
                disabled={readOnly}
                placeholder="Select Urgency Level"
                options={toSelectOpts(lookups.urgencyLevels)}
                onChange={(id) => {
                  const name = lookups.urgencyLevels.find((u) => u.id === id)?.name ?? '';
                  setDraft((d) =>
                    d ? { ...d, urgencyLevelId: id, urgencyLevelName: name } : d,
                  );
                  if (!isHighUrgencyLevel(name)) clearErr('urgencyJustification');
                  clearErr('urgencyLevelId');
                }}
              />
              <FieldError message={errors.urgencyLevelId} />
            </div>
            <div className="ccr-fg">
              <label>Deadline</label>
              <FormattedDateInput
                id="ag-deadline"
                className={inpClass(Boolean(errors.deadline))}
                value={draft.deadline}
                minDate={minDeadlineDate}
                enableYmdViews
                disabled={readOnly}
                onChange={(next) => {
                  setDraft((d) => (d ? { ...d, deadline: next } : d));
                  clearErr('deadline');
                }}
              />
              <FieldError message={errors.deadline} />
            </div>
          </div>
          <div className="ccr-fg">
            <label>
              Urgency Justification{' '}
              {urgencyJustificationRequired ? <span className="req">*</span> : null}
            </label>
            <textarea
              id="ag-urgencyJustification"
              className={inpClass(Boolean(errors.urgencyJustification))}
              rows={3}
              maxLength={MAX_DESC}
              value={draft.urgencyJustification}
              readOnly={readOnly}
              disabled={readOnly}
              onChange={(e) => {
                setDraft((d) =>
                  d ? { ...d, urgencyJustification: sliceMax(e.target.value, MAX_DESC) } : d,
                );
                clearErr('urgencyJustification');
              }}
            />
            <FieldError message={errors.urgencyJustification} />
          </div>
          {sectionActions(10)}
        </ReviewSection>

        {/* Step 11 — Submission (read-only) */}
        <ReviewSection step="Step 11" title="Submission" defaultOpen={false}>
          <ReviewSubBlock title="Request Submitter's Details">
            <ReviewGrid cols={4}>
              <ReviewField label="Submitted Division/ Unit" value={submitter.division} />
              <ReviewField label="Designation" value={submitter.designation} />
              <ReviewField label="Submitted by" value={submitter.name} />
              <span className="ccr-review-grid-spacer" aria-hidden="true" />
              <ReviewField label="Email Address" value={submitter.email} />
              <ReviewField label="Contact Number" value={submitter.contact} />
            </ReviewGrid>
          </ReviewSubBlock>
        </ReviewSection>
      </div>
    </>
  );
}
